import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CloudIcon from "@mui/icons-material/Cloud";

const LabList = (props) => {
  const { results = [] } = props || {};
  const [searchitems, setSearchItems] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchItems(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchItems("");
  }, []);

  // Reset pagination when search changes
  useEffect(() => {
    setPage(0);
  }, [searchitems]);

  // Memoized filtered results
  const filteredResultsList = useMemo(() => {
    if (!results || !Array.isArray(results)) return [];
    
    if (!searchitems) {
      return results;
    }
    
    const searchTerm = searchitems.toLowerCase();
    return results.filter((instance) => {
      const labName = instance.Name?.toLowerCase() || '';
      return labName.includes(searchTerm);
    });
  }, [results, searchitems]);

  const paginatedResultsList = useMemo(() => {
    return filteredResultsList.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredResultsList, page, rowsPerPage]);

  // DataGrid columns (memoized)
  const columns = useMemo(() => [
    { 
      field: 'Name', 
      headerName: 'Lab Name', 
      flex: 1, 
      minWidth: 180, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'Provider', 
      headerName: 'Provider', 
      flex: 1, 
      minWidth: 160, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'No_of_Trainee', 
      headerName: 'No of Trainee', 
      flex: 1, 
      minWidth: 150, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'End_Period', 
      headerName: 'End Date', 
      flex: 1, 
      minWidth: 160, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'Spent', 
      headerName: 'Lab Cost', 
      flex: 1, 
      minWidth: 140, 
      headerAlign: 'center', 
      align: 'center', 
      renderCell: (params) => (
        <Typography variant="body2" color="textPrimary">
          {params.value}
        </Typography>
      )
    },
  ], []);

  return (
    <>
      {/* Search and Controls Section */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontSize: { xs: '1.1rem', sm: '1.25rem' }, 
            fontWeight: 700,
            color: 'text.primary'
          }}
        >
          Cloud Labs
        </Typography>
        <TextField
          placeholder="Search by Lab Name"
          value={searchitems}
          onChange={handleSearchChange}
          size="small"
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 400, md: 500 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchitems && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  aria-label="clear search"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Data Grid Section */}
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {filteredResultsList.length > 0 ? (
          <>
            <DataGrid
              autoHeight
              rows={paginatedResultsList}
              getRowId={(row) => row.Lab_ID}
              columns={columns}
              hideFooter
              disableColumnMenu
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'action.hover',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  fontWeight: 600,
                },
              }}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 75, 100]}
              component="div"
              count={filteredResultsList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': {
                  justifyContent: 'flex-start',
                  paddingLeft: 2,
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
              color: 'text.secondary',
            }}
          >
            <CloudIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" gutterBottom>
              {results.length === 0 
                ? 'No billing data available' 
                : 'No matching labs found'}
            </Typography>
            <Typography variant="body2" align="center" sx={{ maxWidth: 400, mt: 1 }}>
              {results.length === 0
                ? 'No cloud lab billing information found. Billing data will appear here once cloud labs are created and running.'
                : `No labs match your search "${searchitems}". Try adjusting your search terms.`}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default LabList;
